import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import { TIME_INTERVALS } from "shared/lib/constants/time";
import { clsxMerge, getFullEntityName } from "shared/lib/helpers";
import { ContactDetailI } from "shared/lib/interfaces/account";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

export const TIME_ZONE_UNKNOWN_LABEL = "Time zone unknown";

const LOCAL_TIME_FORMAT = "h:mm A z";

/**
 * GLE-8481
 * The local time in `timezone`, or null when the zone is empty or invalid.
 * There is no fallback zone. A wrong zone is worse than no zone.
 */
export const getLocalTimeInZone = (
  timezone: string | null | undefined,
  now: number
) => {
  if (!timezone) {
    return null;
  }

  try {
    return dayjs(now).tz(timezone).format(LOCAL_TIME_FORMAT);
  } catch {
    return null;
  }
};

/**
 * The current time, updated at the start of each minute. The card shows hours
 * and minutes only, so a faster tick only adds renders.
 */
export const useMinuteClock = () => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let intervalId: number | undefined;

    const tick = () => setNow(Date.now());

    const timeoutId = window.setTimeout(
      () => {
        tick();
        intervalId = window.setInterval(tick, TIME_INTERVALS.ONE_MINUTE);
      },
      TIME_INTERVALS.ONE_MINUTE - (Date.now() % TIME_INTERVALS.ONE_MINUTE)
    );

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, []);

  return now;
};

/**
 * GLE-8478, GLE-8481
 * The time zone of the account's lead: the primary contact's zone, else the
 * one zone that all contacts with a zone share. Null when there is none, or
 * when the contacts disagree.
 */
export const getLeadTimeZone = (
  contacts: ContactDetailI[] | undefined,
  primaryContactId: string | null | undefined
) => {
  const primaryZone = contacts?.find(
    (contact) => !!primaryContactId && contact.id === primaryContactId
  )?.timezone;

  if (primaryZone) {
    return primaryZone;
  }

  const zones = new Set(
    contacts?.map((contact) => contact.timezone).filter(Boolean)
  );

  return zones.size === 1 ? (Array.from(zones)[0] as string) : null;
};

/**
 * `date` in `timezone`, with the zone's short name, for example
 * "Oct 28, 10:30 AM CDT". With no valid zone, it uses the caller's own zone,
 * also named. A date with no zone name is ambiguous to a caller. Null for an
 * empty or invalid date.
 */
export const formatDateInZone = (
  date: string | null | undefined,
  format: string,
  timezone: string | null | undefined
) => {
  if (!date) {
    return null;
  }

  const parsed = dayjs(date);

  if (!parsed.isValid()) {
    return null;
  }

  if (timezone) {
    try {
      return parsed.tz(timezone).format(`${format} z`);
    } catch {
      // An invalid zone falls through to the caller's zone
    }
  }

  return parsed.tz(dayjs.tz.guess()).format(`${format} z`);
};

const formatZone = (timezone: string | null | undefined, now: number) => {
  const localTime = getLocalTimeInZone(timezone, now);

  return localTime ? `${localTime} (${timezone})` : TIME_ZONE_UNKNOWN_LABEL;
};

interface ContactLocalTimePropsI extends PropsWithClassNameI {
  timezone?: string | null;
}

/**
 * One contact's local time and zone, or "Time zone unknown".
 */
export const ContactLocalTime = ({
  className,
  timezone,
}: ContactLocalTimePropsI) => {
  const now = useMinuteClock();

  return <span className={className}>{formatZone(timezone, now)}</span>;
};

interface ContactsLocalTimePropsI extends PropsWithClassNameI {
  contacts?: ContactDetailI[];
}

/**
 * The local time of each contact on the account. Contacts in the same zone
 * share one entry, so the line stays short.
 */
export const ContactsLocalTime = ({
  className,
  contacts,
}: ContactsLocalTimePropsI) => {
  const now = useMinuteClock();

  const entries = useMemo(() => {
    const namesByZone = new Map<string, string[]>();

    contacts?.forEach((contact) => {
      const zone = formatZone(contact.timezone, now);
      const names = namesByZone.get(zone) ?? [];

      names.push(getFullEntityName(contact, "Unnamed lead"));
      namesByZone.set(zone, names);
    });

    return Array.from(namesByZone, ([zone, names]) => ({ zone, names }));
  }, [contacts, now]);

  if (entries.length === 0) {
    return null;
  }

  const text = entries
    .map(({ zone, names }) => `${zone}: ${names.join(", ")}`)
    .join(" · ");

  return (
    <div
      className={clsxMerge("flex min-w-0 items-center gap-1", className)}
      title={text}
    >
      <span className="shrink-0 text-[#333] typography-body-5-medium">
        Local time
      </span>

      <span className="truncate typography-body-5">{text}</span>
    </div>
  );
};
